<template>
  <transition name="modal">
    <div v-if="open" class="fixed inset-0 z-[100] flex items-center justify-center p-4 font-NotoSerifKhmer">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-md" @click="handleCloseModal('close')"></div>

      <!-- Modal Content -->
      <div class="relative w-full max-w-md bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-6 md:p-8 animate-scale-up">
        
        <!-- Status Icon -->
        <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 border shadow-lg transition-all duration-500"
          :class="statusString === 'Completed' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20 shadow-emerald-500/10' : 'bg-amber-500/20 text-amber-400 border-amber-500/20 shadow-amber-500/10'"
        >
          <svg v-if="statusString === 'Completed'" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        <!-- Text -->
        <h3 class="text-2xl font-bold text-white mb-2 tracking-tight text-center">Update Status</h3>
        <p class="text-white/60 mb-8 leading-relaxed text-center">
          Update the status for <span class="text-white font-medium">"{{ doc?.eventName || 'this event' }}"</span>
        </p>

        <!-- Status Selection -->
        <div class="space-y-3 mb-8">
          <button 
            @click="statusString = 'Pending'"
            class="w-full p-4 rounded-2xl border transition-all duration-300 flex items-center justify-between group"
            :class="statusString === 'Pending' ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 shadow-lg shadow-amber-500/10' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'"
          >
            <div class="flex items-center gap-3">
              <div class="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
              <span class="font-bold tracking-wider uppercase text-xs">Pending</span>
            </div>
            <div v-if="statusString === 'Pending'" class="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
               <svg class="h-3 w-3 text-slate-900" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </button>

          <button 
            @click="statusString = 'Completed'"
            class="w-full p-4 rounded-2xl border transition-all duration-300 flex items-center justify-between group"
            :class="statusString === 'Completed' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300 shadow-lg shadow-emerald-500/10' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'"
          >
            <div class="flex items-center gap-3">
              <div class="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span class="font-bold tracking-wider uppercase text-xs">Completed</span>
            </div>
            <div v-if="statusString === 'Completed'" class="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
               <svg class="h-3 w-3 text-slate-900" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </button>
        </div>

        <!-- Actions -->
        <div class="flex gap-4">
          <button
            @click="handleCloseModal('close')"
            :disabled="isUpdating"
            class="flex-1 py-3 rounded-xl text-white/70 font-medium hover:bg-white/10 hover:text-white transition-all duration-200 border border-transparent hover:border-white/10"
          >
            Cancel
          </button>
          
          <button
            @click="handleUpdate"
            class="flex-1 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 shadow-lg shadow-indigo-500/20 transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2"
            :class="isUpdating ? 'cursor-not-allowed opacity-80' : ''"
          >
            <span v-if="isUpdating" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            <span>{{ isUpdating ? 'Updating...' : 'Update' }}</span>
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted } from "vue";
import useCollection from "@/composables/useDocument";

const props = defineProps({
  docId: String,
  doc: Object,
});

const emit = defineEmits(["onClose"]);
const open = ref(true);
const isUpdating = ref(false);
const statusString = ref('Pending');

onMounted(() => {
  if (props.doc && props.doc.statusString) {
    statusString.value = props.doc.statusString;
  }
});

const handleCloseModal = (status) => {
  open.value = false;
  setTimeout(() => {
    emit("onClose", status);
  }, 300);
};

const handleUpdate = async () => {
  if (!props.docId) return;
  
  isUpdating.value = true;
  try {
    const { updateDocs } = useCollection('events');
    await updateDocs(props.docId, {
      statusString: statusString.value
    });
    handleCloseModal('update');
  } catch (e) {
    console.error(e);
    handleCloseModal('failed');
  } finally {
    isUpdating.value = false;
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.animate-scale-up {
  animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
